
import React, { useState } from 'react';
import { Plus, Trash2, Home, CreditCard, PieChart, PoundSterling, Briefcase } from 'lucide-react';
import { EstateAssets, PropertyAsset, BankAccountAsset, InvestmentAsset, PensionAsset, DebtLiability } from '../types';
import _ from 'lodash';
import { HelpPopover } from './HelpPopover';
import { HELP_CONTENT } from './helpContent';
import { PROBATE_SCROLL_EVENT, ProbateScrollTarget } from './probate/scrollTargets';

// Helper to generate IDs
const newId = () => Math.random().toString(36).substr(2, 9);

interface EstateBuilderProps {
 data: EstateAssets;
 onChange: (newData: EstateAssets) => void;
}

// Helper to flash highlight
function flashHighlight(el: HTMLElement) {
 el.classList.add("ring-2","ring-offset-2","ring-[#3730A3]");
 setTimeout(() => el.classList.remove("ring-2","ring-offset-2","ring-[#3730A3]"), 1500);
}

// Helper to find scroll parent
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
 let p: HTMLElement | null = el?.parentElement || null;
 while (p) {
 const style = window.getComputedStyle(p);
 const overflowY = style.overflowY;
 if ((overflowY ==="auto" || overflowY ==="scroll") && p.scrollHeight > p.clientHeight) return p;
 p = p.parentElement;
 }
 return null;
}

export const EstateBuilder: React.FC<EstateBuilderProps> = ({ data, onChange }) => {
 const [activeTab, setActiveTab] = useState<'properties' | 'banks' | 'investments' | 'pensions' | 'debts'>('properties');

 // Force HMR: EstateBuilder UI v2
 const propertiesRef = React.useRef<HTMLDivElement>(null);

 React.useEffect(() => {
 const handler = (e: CustomEvent<ProbateScrollTarget>) => {
 const detail = e.detail;
 if (!detail || detail.target !== 'properties') return;

 console.debug("[probate] EstateBuilder received probate:scroll_to", detail);

 // 1. Open Properties Tab
 setActiveTab('properties');

 // 2. Scroll to container
 // We wait a tick to ensure tab render
 setTimeout(() => {
 const container = document.getElementById('estatebuilder-properties');

 if (container) {
 const scrollParent = findScrollParent(container);
 if (scrollParent) {
 const parentRect = scrollParent.getBoundingClientRect();
 const elRect = container.getBoundingClientRect();
 // Scroll to top of list
 scrollParent.scrollTo({
 top: scrollParent.scrollTop + (elRect.top - parentRect.top) - 16,
 behavior:"smooth",
 });
 } else {
 container.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }
 }

 // 3. Focus specific field
 setTimeout(() => {
 const focusKey = detail.focus ||"main_residence";
 // Find first property or"add new"
 const firstProperty = (data.properties || [])[0];

 let focusEl: HTMLElement | null = null;

 if (!firstProperty) {
 focusEl = document.querySelector('[data-probate-action="add_property"]');
 } else {
 // constructing ID based on key
 // main_residence => #main-res-{id}
 // deceased_share => use data attribute or ID if stable. 
 // In render, inputs don't have stable IDs for share/passes-to, let's add them.
 if (focusKey === 'main_residence') {
 focusEl = document.getElementById(`main-res-${firstProperty.id}`);
 } else if (focusKey === 'deceased_share') {
 focusEl = document.getElementById(`share-${firstProperty.id}`);
 } else if (focusKey === 'passes_to') {
 focusEl = document.getElementById(`passes-to-${firstProperty.id}`);
 }
 }

 if (focusEl) {
 focusEl.focus();
 flashHighlight(focusEl);
 }
 }, 150); // wait for scroll
 }, 50); // wait for tab switch
 };

 window.addEventListener(PROBATE_SCROLL_EVENT as any, handler);
 return () => window.removeEventListener(PROBATE_SCROLL_EVENT as any, handler);
 }, [data.properties]);

 // Generic Add Handler
 const addItem = (section: keyof EstateAssets, defaultItem: any) => {
 const currentList = (data[section] as any[]) || [];
 const newList = [...currentList, { ...defaultItem, id: newId() }];
 const newData = { ...data, [section]: newList };

 // Auto-update gateway if needed? Let derivation handle it, or force it here?
 // Let's force gateway to true if we add an item, for consistency
 /* 
 if (section === 'properties' || section === 'bank_accounts') {
 newData.has_any_sole_assets = true; 
 }
 */
 onChange(newData);
 };

 // Generic Remove Handler
 const removeItem = (section: keyof EstateAssets, id: string) => {
 const currentList = (data[section] as any[]) || [];
 const newList = currentList.filter(item => item.id !== id);
 onChange({ ...data, [section]: newList });
 };

 // Generic Update Handler
 const updateItem = (section: keyof EstateAssets, id: string, field: string, value: any) => {
 const currentList = (data[section] as any[]) || [];
 const newList = currentList.map(item => {
 if (item.id === id) {
 return { ...item, [field]: value };
 }
 return item;
 });
 onChange({ ...data, [section]: newList });
 };

 // Renderers
 const renderProperties = () => (
 <div className="space-y-4" id="estatebuilder-properties">
 {(data.properties || []).length === 0 && (
 <div className="p-8 text-center border border-dashed border-[#E8E4DB] rounded bg-white">
 <Home className="w-8 h-8 text-[#AFA99F] mx-auto mb-2" />
 <p className="text-[13px] text-[#6B6762] italic">No properties added yet.</p>
 <p className="text-[11px] text-[#AFA99F] italic mt-1">Add items like the"Family Home" or"Holiday Cottage".</p>
 </div>
 )}
 {(data.properties || []).map((p: PropertyAsset) => (
 <div key={p.id} id={`property-${p.id}`} className="p-4 border border-[#E8E4DB] rounded bg-white flex flex-col gap-4">
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <input
 className="font-serif text-[16px] font-medium text-[#1A1A1A] w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F]"
 placeholder="Property Name (e.g. Family Home)"
 value={p.label || ''}
 onChange={(e) => updateItem('properties', p.id, 'label', e.target.value)}
 />
 <input
 className="text-[11px] text-[#AFA99F] italic w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F] mt-1"
 placeholder="Address (optional)"
 value={p.address || ''}
 onChange={(e) => updateItem('properties', p.id, 'address', e.target.value)}
 />
 </div>
 <button onClick={() => removeItem('properties', p.id)} className="p-1.5 text-[#6B6762] hover:text-[#1A1A1A] hover:bg-[#FAF3E0] rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
 </div>

 <div className="grid grid-cols-2 gap-4">
 {/* 1. Value (residence history is answered in the Properties step) */}
 <div className="col-span-2 flex items-start gap-4">
 <div className="flex-1">
 <label className="block text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Estimated Value at Date of Death (£)</label>
 <input
 type="number"
 className="w-full font-mono tabular-nums text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 placeholder="Market value"
 value={p.estimated_value || ''}
 onChange={(e) => updateItem('properties', p.id, 'estimated_value', Number(e.target.value))}
 />
 </div>
 {/* The RNRB residence history is not edited here: it is the explicit answer in the Properties step, and
     is_main_residence is retired compatibility state (§12; Slice 6). This note keeps the focus target for residence fixes. */}
 <p id={`main-res-${p.id}`} tabIndex={-1} className="pt-6 text-[11px] text-[#6B6762] max-w-[12rem]">
 Whether they lived here as their home is answered in the Properties step.
 </p>
 </div>

 {/* 2. Ownership Type */}
 <div>
 <label className="block text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Ownership Type</label>
 <select
 className="w-full text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 value={p.ownership_type === 'sole' ? 'sole' : (p.ownership_type ? 'joint' : '')}
 onChange={(e) => {
 const val = e.target.value;
 if (val === 'sole') {
 updateItem('properties', p.id, 'ownership_type', 'sole');
 updateItem('properties', p.id, 'deceased_share_percent', 100);
 } else if (val === 'joint') {
 // Set to 'unknown' as a temporary placeholder for"Joint (Specifics Unsure)"
 updateItem('properties', p.id, 'ownership_type', 'unknown');
 } else {
 updateItem('properties', p.id, 'ownership_type', null);
 }
 }}
 >
 <option value="">Select...</option>
 <option value="sole">Sole Name</option>
 <option value="joint">Joint Owners</option>
 </select>
 </div>

 {/* 3. Joint Kind (if joint) */}
 {(p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common' || p.ownership_type === 'unknown') && (
 <div>
 <label className="block text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Joint Ownership Kind</label>
 <select
 className="w-full text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 value={p.ownership_type || ''}
 onChange={(e) => updateItem('properties', p.id, 'ownership_type', e.target.value)}
 >
 <option value="unknown">Select Kind...</option>
 <option value="joint_tenants">Joint Tenants (Passes to survivor)</option>
 <option value="tenants_in_common">Tenants in Common (Willed)</option>
 <option value="unknown">Joint (Unsure which)</option>
 </select>
 <p className="text-[11px] text-[#AFA99F] italic mt-1">
 {p.ownership_type === 'joint_tenants' ?"Usually passes automatically to suriviving owner." :
 p.ownership_type === 'tenants_in_common' ?"Deceased's share can be left in the Will." :""}
 </p>
 </div>
 )}

 {/* 4. Deceased Share */}
 <div>
 <label className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1 flex items-center gap-1">
 Deceased Share (%)
 <HelpPopover content={HELP_CONTENT.DECEASED_HOME_SHARE_PERCENT} />
 </label>
 <div className="relative">
 <input
 type="number"
 id={`share-${p.id}`}
 className="w-full font-mono tabular-nums text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 placeholder={p.ownership_type === 'sole' ?"100" :"e.g. 50"}
 min="0" max="100"
 value={p.deceased_share_percent ?? ''}
 onChange={(e) => updateItem('properties', p.id, 'deceased_share_percent', Math.min(100, Math.max(0, Number(e.target.value))))}
 />
 {p.ownership_type !== 'sole' && (
 <button
 onClick={() => updateItem('properties', p.id, 'deceased_share_percent', 50)}
 className="absolute right-1 top-1.5 px-1.5 py-0.5 bg-[#F3EFE6] text-[10px] text-[#6B6762] rounded border border-[#E8E4DB] hover:bg-[#EEF0FA]"
 title="Quick set to 50%"
 >
 50%
 </button>
 )}
 </div>
 </div>

 {/* 5. Inheritance Destination */}
 <div>
 <label className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1 flex items-center gap-1">
 Who inherits this share?
 <HelpPopover content={HELP_CONTENT.DECEASED_HOME_SHARE_PASSES_TO} />
 </label>
 <select
 id={`passes-to-${p.id}`}
 className="w-full text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 value={p.deceased_share_passes_to || ''}
 onChange={(e) => updateItem('properties', p.id, 'deceased_share_passes_to', e.target.value)}
 >
 <option value="">Select...</option>
 <option value="spouse">Spouse / Civil Partner</option>
 <option value="direct_descendants">Children / Grandchildren</option>
 <option value="other">Someone else</option>
 <option value="trust">Trust</option>
 <option value="unknown">Not sure</option>
 </select>
 </div>

 {/* 6. Partial Inheritance (Conditional) */}
 {(p.deceased_share_passes_to === 'direct_descendants' || p.deceased_share_passes_to === 'unknown') && (
 <div className="col-span-2 bg-[#EEF0FA] p-2 rounded border border-[#C7CBEA] animate-in fade-in slide-in-from-top-1">
 <label className="text-[10px] tracking-[0.18em] uppercase text-[#3730A3] font-semibold mb-1 flex items-center gap-1">
 % of Deceased's Share going to Descendants
 <HelpPopover content={{ what_it_means:"If the share is split, tell us what % of the deceased's share is inherited by children or grandchildren.", why_we_ask:"We apply the Residence Nil Rate Band specifically to the share inherited by qualifying family members." }} />
 </label>
 <div className="flex items-center gap-3">
 <div className="relative w-32">
 <input
 type="number"
 className="w-full font-mono tabular-nums text-[13px] bg-white text-[#1A1A1A] border border-[#C7CBEA] rounded p-1.5 pr-6 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 placeholder="100"
 min="0" max="100"
 value={p.descendant_share_of_deceased_percent ?? ''}
 onChange={(e) => updateItem('properties', p.id, 'descendant_share_of_deceased_percent', Math.min(100, Math.max(0, Number(e.target.value))))}
 />
 <span className="absolute right-2 top-1.5 text-[12px] text-[#3730A3]">%</span>
 </div>
 <p className="text-[11px] text-[#3F3D39] flex-1 leading-snug">
 Usually <strong className="text-[#1A1A1A]">100%</strong> if"Children" selected above. <br />Change this if the deceased share is split further.
 </p>
 </div>
 </div>
 )}

 <div className="col-span-2">
 <label className="block text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">Notes</label>
 <textarea
 className="w-full text-[13px] bg-white text-[#1A1A1A] border border-[#E8E4DB] rounded p-2 h-10 focus:border-[#3730A3] focus:ring-1 focus:ring-[#3730A3]"
 placeholder="Any context for the solicitor..."
 value={p.notes || ''}
 onChange={(e) => updateItem('properties', p.id, 'notes', e.target.value)}
 />
 </div>
 </div>
 </div>
 ))}
 <button
 onClick={() => addItem('properties', { ownership_type: 'sole', is_main_residence: false, has_been_residence: null, deceased_share_percent: 100 })}
 data-probate-action="add_property"
 className="w-full py-3 bg-transparent border border-[#E8E4DB] rounded text-[13px] font-medium text-[#3F3D39] hover:bg-[#F3EFE6] flex items-center justify-center gap-2 transition-colors"
 >
 <Plus className="w-3 h-3" /> Add Property
 </button>
 </div>
 );

 const renderBanks = () => (
 <div className="space-y-4">
 {(data.bank_accounts || []).length === 0 && (
 <div className="p-8 text-center border-2 border-dashed border-[#E8E4DB] rounded bg-[#FAFAF8]/50">
 <p className="text-xs text-[#AFA99F] italic">No bank accounts added.</p>
 </div>
 )}
 {(data.bank_accounts || []).map((b: BankAccountAsset) => (
 <div key={b.id} className="p-4 border border-[#E8E4DB] rounded bg-white flex flex-col gap-3">
 <div className="flex justify-between items-start">
 <input
 className="text-sm font-bold w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F]"
 placeholder="Account Label (e.g. Main Current Account)"
 value={b.label || ''}
 onChange={(e) => updateItem('bank_accounts', b.id, 'label', e.target.value)}
 />
 <button onClick={() => removeItem('bank_accounts', b.id)} className="text-[#AFA99F] hover:text-[#1A1A1A]"><Trash2 className="w-4 h-4" /></button>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-[10px] uppercase text-[#6B6762] font-bold mb-1">Ownership</label>
 <select
 className="w-full text-xs border rounded-lg p-2"
 value={b.ownership || ''}
 onChange={(e) => updateItem('bank_accounts', b.id, 'ownership', e.target.value)}
 >
 <option value="">Select...</option>
 <option value="sole">Sole Name</option>
 <option value="joint">Joint Name</option>
 </select>
 </div>
 <div>
 <label className="block text-[10px] uppercase text-[#6B6762] font-bold mb-1">Balance (£)</label>
 <input
 type="number"
 className="w-full text-xs border rounded-lg p-2"
 placeholder="Amount at death"
 value={b.estimated_value || ''}
 onChange={(e) => updateItem('bank_accounts', b.id, 'estimated_value', Number(e.target.value))}
 />
 </div>
 <div className="col-span-2">
 <label className="block text-[10px] uppercase text-[#6B6762] font-bold mb-1">Who inherits this?</label>
 <select
 className="w-full text-xs border rounded-lg p-2"
 value={b.beneficiary_type || ''}
 onChange={(e) => updateItem('bank_accounts', b.id, 'beneficiary_type', e.target.value)}
 >
 <option value="">Select...</option>
 <option value="spouse">Spouse / Civil Partner</option>
 <option value="other">Someone else</option>
 </select>
 </div>
 </div>
 </div>
 ))}
 <button
 onClick={() => addItem('bank_accounts', { ownership: 'sole' })}
 className="w-full py-2 border-2 border-dashed border-[#E8E4DB] rounded text-[#AFA99F] hover:border-[#E8E4DB] hover:text-[#3F3D39] text-xs font-bold flex items-center justify-center gap-1"
 >
 <Plus className="w-3 h-3" /> Add Bank Account
 </button>
 </div>
 );

 const renderInvestments = () => (
 <div className="space-y-4">
 {(data.investments || []).map((i: InvestmentAsset) => (
 <div key={i.id} className="p-4 border border-[#E8E4DB] rounded bg-white flex flex-col gap-3">
 <div className="flex justify-between items-start">
 <input
 className="text-sm font-bold w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F]"
 placeholder="Investment Type/Provider"
 value={i.label || ''}
 onChange={(e) => updateItem('investments', i.id, 'label', e.target.value)}
 />
 <button onClick={() => removeItem('investments', i.id)} className="text-[#AFA99F] hover:text-[#1A1A1A]"><Trash2 className="w-4 h-4" /></button>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <select
 className="text-xs border rounded-lg p-2"
 value={i.ownership || ''}
 onChange={(e) => updateItem('investments', i.id, 'ownership', e.target.value)}
 >
 <option value="">Ownership...</option>
 <option value="sole">Sole</option>
 <option value="joint">Joint</option>
 </select>
 <input
 type="number"
 className="text-xs border rounded-lg p-2"
 placeholder="Value (£)"
 value={i.estimated_value || ''}
 onChange={(e) => updateItem('investments', i.id, 'estimated_value', Number(e.target.value))}
 />
 <div className="col-span-2">
 <label className="block text-[10px] uppercase text-[#6B6762] font-bold mb-1">Who inherits this?</label>
 <select
 className="w-full text-xs border rounded-lg p-2"
 value={i.beneficiary_type || ''}
 onChange={(e) => updateItem('investments', i.id, 'beneficiary_type', e.target.value)}
 >
 <option value="">Select...</option>
 <option value="spouse">Spouse / Civil Partner</option>
 <option value="other">Someone else</option>
 </select>
 </div>
 </div>
 </div>
 ))}
 <button
 onClick={() => addItem('investments', { ownership: 'sole' })}
 className="w-full py-2 border-2 border-dashed border-[#E8E4DB] rounded text-[#AFA99F] hover:border-[#E8E4DB] hover:text-[#3F3D39] text-xs font-bold flex items-center justify-center gap-1"
 >
 <Plus className="w-3 h-3" /> Add Investment
 </button>
 </div>
 );

 const renderPensions = () => (
 <div className="space-y-4">
 {(data.pensions || []).map((p: PensionAsset) => (
 <div key={p.id} className="p-4 border border-[#E8E4DB] rounded bg-white flex flex-col gap-3">
 <div className="flex justify-between items-start">
 <input
 className="text-sm font-bold w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F]"
 placeholder="Pension Provider (e.g. Nest)"
 value={p.label || ''}
 onChange={(e) => updateItem('pensions', p.id, 'label', e.target.value)}
 />
 <button onClick={() => removeItem('pensions', p.id)} className="text-[#AFA99F] hover:text-[#1A1A1A]"><Trash2 className="w-4 h-4" /></button>
 </div>
 <div className="grid grid-cols-1 gap-3">
 <div className="flex items-center justify-between bg-[#FAFAF8] p-2 rounded-lg border border-[#E8E4DB]">
 <div className="flex flex-col">
 <span className="text-xs font-bold text-[#3F3D39]">Outside Estate?</span>
 <span className="text-[9px] text-[#AFA99F]">Pensions often pay out via nominations</span>
 </div>
 <select
 className="text-xs border rounded p-1 w-32"
 value={p.in_estate === false ? 'out' : p.in_estate === true ? 'in' : ''}
 onChange={(e) => updateItem('pensions', p.id, 'in_estate', e.target.value === 'out' ? false : e.target.value === 'in' ? true : null)}
 >
 <option value="">Select...</option>
 <option value="out">Yes (Exclude)</option>
 <option value="in">No (Include)</option>
 </select>
 </div>
 <div className="relative">
 <span className="absolute left-3 top-2.5 text-[#AFA99F] text-xs">£</span>
 <input
 type="number"
 className="w-full text-xs border rounded-lg p-2 pl-6"
 placeholder="Estimated Pot Value"
 value={p.estimated_value || ''}
 onChange={(e) => updateItem('pensions', p.id, 'estimated_value', Number(e.target.value))}
 />
 </div>
 </div>
 </div>
 ))}
 <button
 onClick={() => addItem('pensions', { in_estate: false })}
 className="w-full py-2 border-2 border-dashed border-[#E8E4DB] rounded text-[#AFA99F] hover:border-[#E8E4DB] hover:text-[#3F3D39] text-xs font-bold flex items-center justify-center gap-1"
 >
 <Plus className="w-3 h-3" /> Add Pension asset
 </button>
 </div>
 );

 const renderDebts = () => (
 <div className="space-y-4">
 {(data.debts || []).map((d: DebtLiability) => (
 <div key={d.id} className="p-4 border border-[#E8E4DB] rounded bg-white flex flex-col gap-3">
 <div className="flex justify-between items-start">
 <input
 className="text-sm font-bold w-full border-none p-0 focus:ring-0 placeholder:text-[#AFA99F]"
 placeholder="Creditor (e.g. Mortgage, Visa)"
 value={d.label || ''}
 onChange={(e) => updateItem('debts', d.id, 'label', e.target.value)}
 />
 <button onClick={() => removeItem('debts', d.id)} className="text-[#AFA99F] hover:text-[#1A1A1A]"><Trash2 className="w-4 h-4" /></button>
 </div>
 <div className="relative">
 <span className="absolute left-3 top-2.5 text-[#AFA99F] text-xs">£</span>
 <input
 type="number"
 className="w-full text-xs border rounded-lg p-2 pl-6"
 placeholder="Amount Outstanding"
 value={d.estimated_value || ''}
 onChange={(e) => updateItem('debts', d.id, 'estimated_value', Number(e.target.value))}
 />
 </div>
 </div>
 ))}
 <button
 onClick={() => addItem('debts', {})}
 className="w-full py-2 border-2 border-dashed border-[#E8E4DB] rounded text-[#AFA99F] hover:border-[#E8E4DB] hover:text-[#3F3D39] text-xs font-bold flex items-center justify-center gap-1"
 >
 <Plus className="w-3 h-3" /> Add Debt or Liability
 </button>
 </div>
 );

 return (
 <div className="bg-[#FAFAF8] border border-[#E8E4DB] rounded overflow-hidden" id="estate-builder-root">
 <div className="bg-white border-b border-[#E8E4DB] px-4 py-3">
 <div className="flex items-center justify-between">
 <h3 className="text-sm font-bold text-[#1A1A1A] flex items-center gap-2">
 <Briefcase className="w-4 h-4 text-[#6B6762]" />
 Assets & Liabilities
 </h3>
 <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#EEF0FA] rounded text-[10px] font-bold text-[#3730A3] border border-[#C7CBEA]">
 <PoundSterling className="w-3 h-3" />
 Itemised Mode
 </div>
 </div>
 <p className="text-[11px] text-[#6B6762] mt-0.5">List the deceased's holdings to drive probate and tax logic.</p>
 </div>

 <div className="flex border-b border-[#E8E4DB] bg-white overflow-x-auto no-scrollbar">
 {[
 { key: 'properties', icon: Home, label: 'Property' },
 { key: 'banks', icon: CreditCard, label: 'Cash' },
 { key: 'investments', icon: PieChart, label: 'Invests' },
 { key: 'pensions', icon: Briefcase, label: 'Pensions' },
 { key: 'debts', icon: PoundSterling, label: 'Debts' },
 ].map(tab => (
 <button
 key={tab.key}
 onClick={() => setActiveTab(tab.key as any)}
 className={`flex items-center gap-2 px-4 py-3 text-[11px] font-bold border-b-2 transition-all whitespace-nowrap
 ${activeTab === tab.key ? 'border-[#3730A3] text-[#3730A3] bg-[#EEF0FA]/30' : 'border-transparent text-[#AFA99F] hover:bg-[#FAFAF8] hover:text-[#3F3D39]'}
 `}
 >
 <tab.icon className="w-3.5 h-3.5" />
 {tab.label}
 <span className={`px-1.5 rounded-full text-[9px] ${activeTab === tab.key ? 'bg-[#EEF0FA] text-[#3730A3]' : 'bg-[#FAFAF8] text-[#6B6762]'}`}>
 {(data[tab.key === 'banks' ? 'bank_accounts' : tab.key as keyof EstateAssets] as any[])?.length || 0}
 </span>
 </button>
 ))}
 </div>

 <div className="p-4 bg-[#FAFAF8] max-h-[450px] overflow-y-auto">
 {activeTab === 'properties' && renderProperties()}
 {activeTab === 'banks' && renderBanks()}
 {activeTab === 'investments' && renderInvestments()}
 {activeTab === 'pensions' && renderPensions()}
 {activeTab === 'debts' && renderDebts()}
 </div>

 <div className="bg-white border-t border-[#E8E4DB] p-3 flex justify-center">
 <p className="text-[10px] text-[#AFA99F] text-center max-w-xs">
 Information added here will update the Case Summary and IHT estimates automatically.
 </p>
 </div>
 </div>
 );
};
